import React from 'react'
import PropTypes from 'prop-types'
import { TextInput, TimerInput } from 'components/patterns/Inputs'
import { RadioButtonGroup, DrawerDivider } from 'components/patterns'
import { Paragraph, Header } from 'components/patterns/Typography'
import { ReadOnlyParameterValue } from 'components/feature/NavigatorPanes/editors/base'
import OutputtingDataTo from 'components/feature/NavigatorPanes/OutputtingDataTo'
import Editor from 'components/feature/Editor'
import strings from 'utils/strings'
import { timerValue } from 'utils/timers'
import { M0, M1, M2, TIMER } from 'utils/constants'
import { TIMER_MASK_HMMSSS } from 'constants/ui/timers'

import querySchema from 'schemas/validation/feature/querySchema'
import { schemaProp } from 'schemas/validation'

import { compose, map } from 'ramda'
import ConditionLabel from 'components/feature/ConditionLabel/ConditionLabelContainer'
import TimerModeDescription from './TimerModeDescription'
import withStyle from './TimerBoxEditor.style.js'
import { ViewNotesButton } from '../NavigatorPanes/FeatureNotes/FeatureNotes'

const rule = schemaProp(querySchema)

const modes = [M0, M1, M2]
const formatModeButtonData = (m) => ({
  value: m,
  text: strings.timer_mode_labels[m],
})
const modeButtons = map(formatModeButtonData)(modes)

const TimerBoxEditor = ({
  className,
  description,
  timerMode,
  updateTimerMode,
  updateTimerValue,
  initialValue,
  updateDescription,
  uri,
  toggleMarkForDeletion,
  deleteTimer,
  markedForDeletion,
  toggleMask,
  closeDrawer,
  resetTimer,
  disableDrawerHandler,
  featureInDesign,
  allDescriptions,
  conditions = [],
  isOrphan,
  isSimulating,
  isEditing,
}) => {
  const timerBoxEditable = isEditing && featureInDesign

  if (!uri) return ''

  return (
    <Editor
      className={className}
      toggleMarkForDeletion={toggleMarkForDeletion}
      markedForDeletion={markedForDeletion}
      deleteItem={deleteTimer}
      deleteHeaderText={strings.delete_item_heading(description)}
      deleteExplanation={strings.delete_timer_explanation}
      toggleMask={toggleMask}
      closeDrawer={closeDrawer}
      clearItem={resetTimer}
      clearHeaderText={strings.clear_item_heading(description)}
      clearExplanation={strings.clear_timer_explanation}
      disableDrawerHandler={disableDrawerHandler}
    >
      {!isOrphan && (
        <ViewNotesButton id={uri} fromType={TIMER} description={description} />
      )}
      <Header type="h3" className="form-title">
        {description}
      </Header>
      {timerBoxEditable ? (
        <>
          <div className="timer-name-input">
            <TextInput
              rules={rule('description', {
                descriptions: allDescriptions,
              })}
              label={strings.timer_name}
              name={strings.description}
              value={description}
              onChange={updateDescription}
            />
          </div>

          <TimerInput
            label={strings.timer_initial_value_label}
            hint={strings.timer_initial_value_hint}
            value={initialValue}
            onChange={updateTimerValue}
            name={strings.timer_initial_value_label}
            mask={TIMER_MASK_HMMSSS}
          />

          <RadioButtonGroup
            vertical
            label={strings.mode}
            onChange={updateTimerMode}
            currentValue={timerMode}
            buttonStyle="solid"
            buttons={modeButtons}
            radioGroupClassName="timer-mode-radio-button-group"
          />
        </>
      ) : (
        <>
          <DrawerDivider />
          <ReadOnlyParameterValue
            label={strings.timer_initial_value_label}
            value={timerValue(initialValue)}
            size="small"
          />
          <DrawerDivider />
          <Paragraph weight="bold" size="small">
            {strings.timer_mode_label} {strings.timer_mode_numbers[timerMode]}
          </Paragraph>
        </>
      )}

      <TimerModeDescription
        mode={timerMode}
        initialValue={initialValue}
        uri={uri}
      />

      {!isEditing && <DrawerDivider />}

      <Paragraph size="small">
        {conditions.map((condition, index) => (
          <React.Fragment key={condition}>
            <ConditionLabel
              vertical
              className="editor-section"
              label={
                index === 0
                  ? strings.activate_query_label
                  : strings.reset_query_label
              }
              data-testid={
                index === 0 ? 'activate-condition-link' : 'reset-condition-link'
              }
              key={condition}
              parentQueryUri={uri}
              uri={condition}
              parentType={TIMER}
              isLink
            />
          </React.Fragment>
        ))}
      </Paragraph>
      <OutputtingDataTo uri={uri} />
    </Editor>
  )
}

TimerBoxEditor.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  updateDescription: PropTypes.func,
  timerMode: PropTypes.string,
  updateTimerMode: PropTypes.func,
  updateTimerValue: PropTypes.func,
  initialValue: PropTypes.number,
  onValidityChange: PropTypes.func,
  uri: PropTypes.string,
  onEditorChange: PropTypes.func,
  toggleMarkForDeletion: PropTypes.func,
  toggleMask: PropTypes.func,
  deleteTimer: PropTypes.func,
  markedForDeletion: PropTypes.bool,
  closeDrawer: PropTypes.func,
  resetTimer: PropTypes.func,
  disableDrawerHandler: PropTypes.func,
  featureInDesign: PropTypes.bool,
  allDescriptions: PropTypes.arrayOf(PropTypes.string),
  conditions: PropTypes.array,
  isEditing: PropTypes.bool,
  isSimulating: PropTypes.bool,
  isOrphan: PropTypes.bool,
}

TimerModeDescription.propTypes = {
  mode: PropTypes.string.isRequired,
  className: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

export default compose(withStyle)(TimerBoxEditor)
